import { advertisingPaymentCreateSchema, todayInTimezone, toDecimal } from '@mm/shared';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { type FieldDef, ResourceForm } from '../../forms/ResourceForm';
import { formatMoney } from '../../utils/format';

/** Used vs limit bar; turns amber above 75% and red above 90%. */
export function CreditBar({ used, limit }: { used: string; limit: string | null }) {
  if (!limit || toDecimal(limit).isZero())
    return <span className="text-xs text-slate-400">No credit line</span>;
  const pct = Math.max(0, Math.min(100, toDecimal(used).dividedBy(limit).times(100).toNumber()));
  const color = pct >= 90 ? 'bg-rose-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-slate-100"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Credit used"
      >
        <div className={clsx('h-full rounded-full', color)} style={{ width: `${pct}%` }} />
      </div>
      <p className="num mt-1 text-xs text-slate-500">
        {formatMoney(used)} of {formatMoney(limit)} used ({pct.toFixed(1)}%)
      </p>
    </div>
  );
}

const paymentFields: FieldDef[] = [
  { name: 'paymentDate', label: 'Payment date', type: 'date', required: true },
  { name: 'amount', label: 'Amount (₹)', type: 'decimal', required: true },
  {
    name: 'reference',
    label: 'Reference',
    type: 'text',
    placeholder: 'UTR / NEFT / card reference',
  },
  { name: 'notes', label: 'Notes', type: 'textarea', wide: true },
];

/** Record a credit-line payment (MCC) or a prepaid top-up (advertising account). */
export function PaymentDialog({
  target,
  onClose,
}: {
  target: { kind: 'mcc' | 'prepaid'; id: string; name: string } | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      size="sm"
      title={target?.kind === 'mcc' ? 'Record credit line payment' : 'Record prepaid top-up'}
      description={target?.name}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="payment-form">
            Save
          </Button>
        </>
      }
    >
      {target && (
        <ResourceForm
          formId="payment-form"
          fields={paymentFields}
          schema={advertisingPaymentCreateSchema}
          isEdit={false}
          lookups={undefined}
          defaultValues={{
            paymentDate: todayInTimezone(),
            amount: '',
            reference: '',
            notes: '',
            managerAccountId: target.kind === 'mcc' ? target.id : '',
            advertisingAccountId: target.kind === 'prepaid' ? target.id : '',
          }}
          onSubmit={async (values) => {
            await api.post('/advertising-payments', values);
            toast.success(
              target.kind === 'mcc'
                ? 'Payment recorded — available credit updated'
                : 'Top-up recorded',
            );
            void queryClient.invalidateQueries({ queryKey: ['billing'] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            onClose();
          }}
        />
      )}
    </Modal>
  );
}
